import {
  currentAccountStorageScope,
  listAccountScopedStorage,
  removeAccountScopedStorage,
  writeAccountScopedStorage,
} from "@/shared/storage/account-scope";
import { pendingSyncDeletions } from "@/shared/sync/deletion-outbox";

const GOOGLE_TASK_CANONICAL_DELETION_FENCE_PREFIX =
  "tb_public_web_google_task_canonical_delete_v1:";
const memoryFencesByScope = new Map<string, Set<string>>();

function cleanClientId(value: unknown): string | null {
  const clientId = String(value || "").trim();
  return clientId && clientId.length <= 512 ? clientId : null;
}

function fenceBaseKey(clientId: string): string {
  return `${GOOGLE_TASK_CANONICAL_DELETION_FENCE_PREFIX}${encodeURIComponent(clientId)}`;
}

function memoryFences(): Set<string> {
  const scope = currentAccountStorageScope();
  let fences = memoryFencesByScope.get(scope);
  if (!fences) {
    fences = new Set();
    memoryFencesByScope.set(scope, fences);
  }
  return fences;
}

/**
 * Keeps a stale provider mirror hidden from the instant its canonical local
 * owner is deleted until undo/recreate or an explicit canonical tombstone.
 * This fence is account-scoped and lifecycle-owned; it is not inferred from
 * a missing row or a generic sync result.
 */
export function fenceDeletedGoogleTaskCanonicalClientId(value: unknown): void {
  const clientId = cleanClientId(value);
  if (!clientId) return;
  memoryFences().add(clientId);
  try {
    writeAccountScopedStorage(fenceBaseKey(clientId), clientId);
  } catch {
    // The in-memory fence still protects this tab when storage is unavailable.
  }
}

export function clearDeletedGoogleTaskCanonicalClientId(value: unknown): void {
  const clientId = cleanClientId(value);
  if (!clientId) return;
  memoryFences().delete(clientId);
  try {
    removeAccountScopedStorage(fenceBaseKey(clientId));
  } catch {
    // A failed storage removal cannot make the current tab reveal the mirror.
  }
}

export function deletedGoogleTaskCanonicalClientIds(): Set<string> {
  const result = new Set(memoryFences());
  // The outbox is written before the local task row is removed. Treat it as a
  // second durable fence if the dedicated (smaller) storage write was rejected
  // at quota, including after a process restart. The dedicated fence remains
  // authoritative after the outbox is acknowledged.
  for (const deletion of pendingSyncDeletions().deletedTodos) {
    const clientId = cleanClientId(deletion.clientId);
    if (clientId) result.add(clientId);
  }
  for (const { baseKey, value } of listAccountScopedStorage(
    GOOGLE_TASK_CANONICAL_DELETION_FENCE_PREFIX,
  )) {
    const encodedSlot = baseKey.slice(GOOGLE_TASK_CANONICAL_DELETION_FENCE_PREFIX.length);
    let slotClientId: string | null = null;
    try {
      slotClientId = cleanClientId(decodeURIComponent(encodedSlot));
    } catch {
      slotClientId = null;
    }
    const storedClientId = cleanClientId(value);
    if (slotClientId && storedClientId === slotClientId) result.add(slotClientId);
  }
  return result;
}

type CanonicalTaskSnapshotRow = {
  canonicalClientId?: unknown;
  canonicalState?: unknown;
  linkedClientId?: unknown;
};

/**
 * A missing provider row is not deletion proof: an older queued create can
 * still land after that list snapshot. Retire only an explicit server
 * tombstone; undo/recreate clears the same fence through the task store.
 */
export function reconcileDeletedGoogleTaskCanonicalClientIds(
  tasks: Iterable<CanonicalTaskSnapshotRow>,
): void {
  for (const task of tasks) {
    const canonicalClientId =
      cleanClientId(task.canonicalClientId) || cleanClientId(task.linkedClientId);
    if (task.canonicalState === "tombstoned") {
      clearDeletedGoogleTaskCanonicalClientId(canonicalClientId);
    }
  }
}
